// API client for the Cloudflare Worker backend
// Replaces Supabase auth — stores session token in localStorage

const API_BASE = '/api';

export interface AuthUser {
  user_id: string;
  name: string;
  email: string;
  company_name: string | null;
  user_type?: 'agent' | 'partner';
  is_admin?: boolean;
}

export interface Subscription {
  id: number;
  user_id: string;
  plan: string;
  status: string;
  with_drone: boolean;
  credits_remaining: number;
  credits_total: number;
  credits_used: number;
  has_used_trial: boolean;
  created_at: string;
  updated_at: string | null;
  /** Credits valid until (ISO); null if no credits or no expiry */
  credits_expire_at?: string | null;
  /** 'fr' = euros, 'us' or missing = dollars */
  stripe_region?: string;
  /** Count of 1-credit top-ups bought since monthly subscription started */
  extra_credits_purchased?: number;
  /** Count of 1-credit top-ups bought during the current month */
  extra_credits_purchased_this_month?: number;
  /** Remaining credits from those 1-credit top-ups */
  extra_credits_remaining?: number;
}

export interface Order {
  id: number;
  order_id: string;
  user_id: string;
  user_email: string;
  property_name: string | null;
  matterport_url: string | null;
  status: string;
  property_data: string | null;
  created_at: string;
}

// ─── Token storage ────────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function setToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

export function clearAuth(): void {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('has_active_plan');
}

export function setHasActivePlan(v: boolean): void {
  localStorage.setItem('has_active_plan', v ? '1' : '0');
}

export function getHasActivePlan(): boolean {
  return localStorage.getItem('has_active_plan') === '1';
}

export function getCachedUser(): AuthUser | null {
  const stored = localStorage.getItem('auth_user');
  try {
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

// ─── Fetch wrapper ────────────────────────────────────────────────────────────

/** Erreur enrichie d'un statut HTTP pour distinguer 401/403 (auth expirée) des autres erreurs. */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<unknown> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, cache: 'no-store', credentials: 'include' });
  const text = await res.text();

  let data: unknown;
  const contentType = res.headers.get('Content-Type') || '';
  if (contentType.includes('application/json') && text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError('Réponse serveur invalide. Réessayez.', res.status);
    }
  } else {
    if (!res.ok && text.startsWith('<!')) {
      const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const requestedUrl = typeof window !== 'undefined' ? window.location.origin + `${API_BASE}${path}` : `${API_BASE}${path}`;
      throw new ApiError(
        isLocal
          ? 'En local, les appels /api passent par le proxy vers 3dtovideo.com. Vérifiez que le site est déployé ou testez sur https://3dtovideo.com.'
          : `L’API a renvoyé une page HTML au lieu de JSON (requête : ${requestedUrl}). Voir DEPLOY.md. Testez cette URL dans un onglet : si vous voyez du JSON, videz le cache du site (Ctrl+Shift+R) et réessayez.`,
        res.status,
      );
    }
    data = {};
  }

  if (!res.ok) {
    const msg = (data as { detail?: string; message?: string }).detail
      || (data as { detail?: string; message?: string }).message
      || `Erreur ${res.status}`;
    throw new ApiError(msg, res.status);
  }
  return data;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  async signup(name: string, email: string, password: string, company_name?: string, user_type?: 'agent' | 'partner'): Promise<AuthUser> {
    const data = await apiFetch('/signup', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, company_name, user_type }),
    }) as AuthUser & { token: string };

    setToken(data.token);
    const user: AuthUser = {
      user_id: data.user_id,
      name: data.name,
      email: data.email,
      company_name: data.company_name,
      user_type: data.user_type === 'partner' ? 'partner' : 'agent',
    };
    localStorage.setItem('auth_user', JSON.stringify(user));
    return user;
  },

  /**
   * Connexion. `partnerAuth: true` si la page est /partners/* ou /fr/partenaires/* (enregistre user_type = partner en base).
   */
  async login(email: string, password: string, options?: { partnerAuth?: boolean }): Promise<AuthUser> {
    const data = await apiFetch('/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        ...(options?.partnerAuth ? { partner_auth: true } : {}),
      }),
    }) as AuthUser & { token: string };

    setToken(data.token);
    const user: AuthUser = {
      user_id: data.user_id,
      name: data.name,
      email: data.email,
      company_name: data.company_name,
      user_type: data.user_type === 'partner' ? 'partner' : 'agent',
    };
    localStorage.setItem('auth_user', JSON.stringify(user));
    return user;
  },

  /** Passe le compte en partner (D1) — appelé depuis le TB /partners/dashboard ou /fr/partenaires/dashboard si encore agent. */
  async claimPartnerRole(): Promise<void> {
    await apiFetch('/user/claim-partner-role', { method: 'POST', body: '{}' });
  },

  async logout(): Promise<void> {
    try {
      await apiFetch('/logout', { method: 'POST' });
    } finally {
      clearAuth();
    }
  },

  async getUser(): Promise<AuthUser | null> {
    if (!getToken()) return null;
    try {
      const data = await apiFetch('/user') as AuthUser;
      const prev = getCachedUser();
      /** Si l’API omet user_type, ne pas écraser « partner » du cache avec « agent ». */
      let userType: 'partner' | 'agent';
      if (data.user_type === 'partner') userType = 'partner';
      else if (data.user_type === 'agent') userType = 'agent';
      else if (prev?.user_type === 'partner' || prev?.user_type === 'agent') userType = prev.user_type;
      else userType = 'agent';

      const user: AuthUser = {
        user_id: data.user_id,
        name: data.name,
        email: data.email,
        company_name: data.company_name,
        user_type: userType,
        is_admin: data.is_admin,
      };
      localStorage.setItem('auth_user', JSON.stringify(user));
      return user;
    } catch {
      return null;
    }
  },

  /** Échanger un jeton à usage unique (après Google OAuth) contre la session. Retourne token + return_to. */
  async completeOAuth(oneTimeToken: string): Promise<{ token: string; return_to: string }> {
    const data = await apiFetch('/auth/complete', {
      method: 'POST',
      body: JSON.stringify({ t: oneTimeToken }),
    }) as { token: string; return_to: string };
    return data;
  },

  /** Envoyer un email de réinitialisation (mot de passe oublié). locale: 'fr' | 'en' pour la langue de l'email. */
  async forgotPassword(email: string, locale?: 'fr' | 'en'): Promise<void> {
    await apiFetch('/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim().toLowerCase(), locale: locale || 'en' }),
    });
  },

  /** Réinitialiser le mot de passe avec le token reçu par email. */
  async resetPassword(token: string, new_password: string): Promise<void> {
    await apiFetch('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token: token.trim(), new_password }),
    });
  },
};

// ─── Subscriptions ────────────────────────────────────────────────────────────

export const subscriptionApi = {
  async get(): Promise<Subscription> {
    return await apiFetch('/subscription') as Subscription;
  },

  async update(fields: Partial<Subscription>): Promise<Subscription> {
    return await apiFetch('/subscription', {
      method: 'PUT',
      body: JSON.stringify(fields),
    }) as Subscription;
  },
};

// ─── Orders ───────────────────────────────────────────────────────────────────

export const ordersApi = {
  async list(): Promise<Order[]> {
    return await apiFetch('/orders') as Order[];
  },

  async create(payload: { property_name: string; matterport_url: string; order_details?: unknown; locale?: string }): Promise<{ success: boolean; order_id: string }> {
    return await apiFetch('/orders', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as { success: boolean; order_id: string };
  },
};

// ─── Admin (liste des inscrits — réservé aux admins) ─────────────────────────

export interface AdminUser {
  user_id: string;
  name: string;
  email: string;
  company_name: string | null;
  created_at: string;
}

export const adminApi = {
  async listUsers(): Promise<AdminUser[]> {
    return await apiFetch('/admin/users') as AdminUser[];
  },

  /** Réinitialiser le mot de passe d'un utilisateur (par email). Réservé aux admins. */
  async resetPassword(email: string, new_password: string): Promise<void> {
    await apiFetch('/admin/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim().toLowerCase(), new_password }),
    });
  },
};

// ─── Stripe (checkout + portal), region: us = 3dtovideo.com, fr = /fr ────────

export function getStripeRegion(): 'us' | 'fr' {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return path.startsWith('/fr') ? 'fr' : 'us';
}

/**
 * Dashboard selon le compte (partner vs agent) et la zone (/fr vs US).
 * Invité : repli sur le chemin (ex. /partners/signin → partenaires).
 */
export function getDashboardPathForUser(
  user: Pick<AuthUser, 'user_type'> | null | undefined,
  pathnameHint?: string
): string {
  const raw = pathnameHint || (typeof window !== 'undefined' ? window.location.pathname : '') || '';
  const p = raw.replace(/\/$/, '') || '/';
  const pl = p.toLowerCase();
  const isFr = pl.startsWith('/fr');

  /** En priorité : l’URL courante (retour Stripe annulation / précédent doit rester sur le TB partenaires). */
  if (pl.startsWith('/fr/partenaires')) {
    return '/fr/partenaires/dashboard';
  }
  if (pl.startsWith('/partners')) {
    return '/partners/dashboard';
  }

  const ut = user?.user_type ?? getCachedUser()?.user_type;

  if (ut === 'partner') {
    return isFr ? '/fr/partenaires/dashboard' : '/partners/dashboard';
  }
  if (ut === 'agent') {
    return isFr ? '/fr/dashboard' : '/dashboard';
  }

  if (isFr) return '/fr/dashboard';
  return '/dashboard';
}

/** Cache utilisateur + URL actuelle (checkout, etc.). */
export function getDashboardPathForCurrentPage(): string {
  return getDashboardPathForUser(getCachedUser(), typeof window !== 'undefined' ? window.location.pathname : '');
}

export const paymentApi = {
  async createCheckoutSession(params: {
    region?: 'us' | 'fr';
    type: 'subscription' | 'trial' | 'extra_credit';
    plan_id?: string;
    with_drone?: boolean;
    company_name?: string;
    /** US uniquement : quel JSON Stripe utiliser (`STRIPE_PRICES_US` vs `STRIPE_PRICES_US_PARTNERS`). */
    stripe_us_price_set?: 'home' | 'partners';
    /** Override checkout return URLs. Chemin relatif (ex. "/onboarding/plan") ou URL absolue. */
    success_url?: string;
    cancel_url?: string;
  }): Promise<{ url: string }> {
    const region = params.region ?? getStripeRegion();
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const dashboardPath = getDashboardPathForCurrentPage();
    const pathLower = (typeof window !== 'undefined' ? window.location.pathname : '').toLowerCase();
    const defaultUsStripeSet =
      pathLower.startsWith('/partners') && !pathLower.startsWith('/fr') ? 'partners' : 'home';
    const toAbsoluteUrl = (value: string | undefined): string | null => {
      if (!value) return null;
      if (/^https?:\/\//i.test(value)) return value;
      const path = value.startsWith('/') ? value : '/' + value;
      return base + path;
    };
    const resolvedSuccessUrl = toAbsoluteUrl(params.success_url) ?? base + dashboardPath + '?subscription=success';
    const resolvedCancelUrl = toAbsoluteUrl(params.cancel_url) ?? base + dashboardPath + '?subscription=cancelled';
    const body: Record<string, unknown> = {
      region,
      type: params.type,
      plan_id: params.plan_id,
      with_drone: !!params.with_drone,
      success_url: resolvedSuccessUrl,
      cancel_url: resolvedCancelUrl,
    };
    if (region === 'us') {
      body.stripe_us_price_set = params.stripe_us_price_set ?? defaultUsStripeSet;
    }
    if (params.company_name?.trim()) body.company_name = params.company_name.trim();
    return apiFetch('/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ url: string }>;
  },

  async createPortalSession(return_url?: string): Promise<{ url: string }> {
    if (typeof window !== 'undefined' && !return_url) {
      return_url = window.location.origin + window.location.pathname;
    }
    return apiFetch('/stripe/create-portal-session', {
      method: 'POST',
      body: JSON.stringify({ return_url }),
    }) as Promise<{ url: string }>;
  },

  /** Confirme le checkout au retour de Stripe et crédite le compte immédiatement. */
  async confirmCheckoutSession(session_id: string, region: 'us' | 'fr'): Promise<{ ok: boolean }> {
    return apiFetch('/stripe/confirm-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ session_id, region }),
    }) as Promise<{ ok: boolean }>;
  },
};

// ─── Leads (public) ───────────────────────────────────────────────────────────

export const leadsApi = {
  async submitCustomQuote(params: {
    name: string;
    company_name: string;
    email: string;
    website: string;
    properties_per_month: string;
    project_details?: string;
  }): Promise<{ ok: boolean }> {
    return apiFetch('/custom-quote', {
      method: 'POST',
      body: JSON.stringify(params),
    }) as Promise<{ ok: boolean }>;
  },
};
